/**
 * People (`/business/people`, TMMRSL-0030, H §5 per tenant): the open tenant's customers,
 * staff and invitations on saas-auth, addressed by the numeric tenant id from the workspace
 * (`useTenant().id`), never the slug.
 *
 *  - **Customers** — `GET /tenants/{id}/users` (searched, paged). Role is derived (`USER` once
 *    claimed, else `GUEST`), so a customer row offers disable / enable and remove only.
 *  - **Staff** — `GET /tenants/{id}/staff`. Change role (`OWNER | ADMIN | STAFF` — saas-auth
 *    refuses a role across the staff / customer tables), disable and remove (each confirmed), enable.
 *  - **Invitations** — `GET /tenants/{id}/invitations?status=`, resend / revoke.
 *  - **Invite** — `POST /tenants/{id}/users { email, role }` (no `appId`), always `202`: a neutral
 *    toast. `403 RESELLER_MEMBER_NOT_INVITABLE` is the email field's error (saas-auth returns it
 *    in several cases since SASATH-0342); `409 ALREADY_ACTIVE_MEMBER` /
 *    `INVITATION_ALREADY_PENDING` and the `429`s are inline.
 *
 * STAFF is read-only: no Invite, no row actions, no Resend / Revoke.
 */
import { useCallback, useId, useRef, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  saasAuthResellerApi,
  type Invitation,
  type PageQuery,
  type SaasPage,
  type TenantMember,
  type TenantMemberStatus,
  type TenantRole,
} from '@/api/saasAuthReseller';
import { useCanWrite } from '@/auth/authHooks';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { ApiErrorState } from '@/components/ApiErrorState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { visuallyHidden } from '@/components/visuallyHidden';
import { useToast } from '@/components/useToast';
import { useLocale } from '@/i18n/useLocale';
import { enumParam, useUrlState } from '@/components/useUrlState';
import { INVITATIONS_PARAM, InvitationsTable } from '@/features/members/InvitationsTable';
import { InviteMemberDialog } from '@/features/members/InviteMemberDialog';
import { useMemberError, useMemberErrorMessage, type MemberError } from '@/features/members/memberErrors';
import { StatusChip } from '@/components/status/StatusChip';
import { dayOf, roleLabel } from '@/features/members/memberLabels';
import { Pager, SearchField } from '@/features/members/PagedSearch';
import { RoleDialog, type RoleOption } from '@/features/members/RoleDialog';
import { RowActionsMenu, type RowAction } from '@/features/members/RowActionsMenu';
import { useMenuFocusReturn } from '@/features/members/useMenuFocusReturn';
import { usePagedSearch } from '@/features/members/usePagedSearch';
import { useRemote } from '@/components/useRemote';
import { useTenant } from '@/tenant/useTenant';

const STAFF_ROLES: readonly TenantRole[] = ['OWNER', 'ADMIN', 'STAFF'];
/** Invite roles: a customer (`USER`, the default) or one of the staff roles. */
const INVITE_ROLES: readonly TenantRole[] = ['USER', 'STAFF', 'ADMIN', 'OWNER'];

type PeopleTab = 'customers' | 'staff' | 'invitations';

/** The tab in the URL; a switch drops the old tab's search, page, size and status (TMM-B6). */
const TAB_SCHEMA = { tab: enumParam<PeopleTab>(['customers', 'staff', 'invitations'], 'customers') };
const TAB_OPTIONS = { clears: ['q', 'page', 'size', INVITATIONS_PARAM] };

export function PeopleScreen() {
  const { t } = useTranslation();
  const canWrite = useCanWrite();
  const tenantId = useTenant().id;
  const tabState = useUrlState(TAB_SCHEMA, TAB_OPTIONS);
  const { tab } = tabState.values;
  const setTab = (next: PeopleTab) => tabState.set({ tab: next });
  const [inviting, setInviting] = useState(false);
  const [invitationsKey, setInvitationsKey] = useState(0);

  const inviteRoles: RoleOption[] = INVITE_ROLES.map((role) => ({
    value: role,
    label: role === 'USER' ? t('people.role.customer') : t(`roles.${role}`),
  }));

  const listCustomers = useCallback(
    (query: PageQuery) => saasAuthResellerApi.listTenantUsers(tenantId, query),
    [tenantId],
  );
  const listStaff = useCallback(
    (query: PageQuery) => saasAuthResellerApi.listTenantStaff(tenantId, query),
    [tenantId],
  );
  const listInvitations = useCallback(
    (status: string | undefined) => saasAuthResellerApi.listTenantInvitations(tenantId, status),
    [tenantId],
  );
  const resend = useCallback(
    (id: number) => saasAuthResellerApi.resendTenantInvitation(tenantId, id),
    [tenantId],
  );
  const revoke = useCallback(
    (id: number) => saasAuthResellerApi.revokeTenantInvitation(tenantId, id),
    [tenantId],
  );
  const canActOnInvitation = useCallback(
    (invitation: Invitation) => canWrite && invitation.status === 'PENDING',
    [canWrite],
  );

  return (
    <Box>
      <PageHeader
        id="people"
        actions={
          canWrite ? (
            <Button variant="contained" onClick={() => setInviting(true)}>
              {t('people.invite')}
            </Button>
          ) : null
        }
      />
      <Tabs
        value={tab}
        onChange={(_, next: PeopleTab) => setTab(next)}
        aria-label={t('people.tabsLabel')}
        sx={{ mb: 2 }}
      >
        <Tab value="customers" label={t('people.tab.customers')} />
        <Tab value="staff" label={t('people.tab.staff')} />
        <Tab value="invitations" label={t('people.tab.invitations')} />
      </Tabs>
      {tab === 'customers' ? (
        <Members key="customers" kind="customers" list={listCustomers} />
      ) : null}
      {tab === 'staff' ? <Members key="staff" kind="staff" list={listStaff} /> : null}
      {tab === 'invitations' ? (
        <InvitationsTable
          key={invitationsKey}
          list={listInvitations}
          resend={resend}
          revoke={revoke}
          canAct={canActOnInvitation}
          {...(canWrite ? { onInvite: () => setInviting(true) } : {})}
        />
      ) : null}
      <InviteMemberDialog
        open={inviting}
        title={t('people.inviteTitle')}
        roles={inviteRoles}
        defaultRole="USER"
        onInvite={({ email, role }) =>
          saasAuthResellerApi.inviteTenantUser(tenantId, { email, role: role as TenantRole })
        }
        onClose={() => setInviting(false)}
        onDone={() => {
          setInvitationsKey((key) => key + 1);
          setTab('invitations');
        }}
      />
    </Box>
  );
}

type Pending =
  | { kind: 'role'; member: TenantMember }
  | { kind: 'disable'; member: TenantMember }
  | { kind: 'remove'; member: TenantMember }
  | null;

function Members({
  kind,
  list,
}: {
  kind: 'customers' | 'staff';
  list: (query: PageQuery) => Promise<SaasPage<TenantMember>>;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const showToast = useToast();
  const canWrite = useCanWrite();
  const tenantId = useTenant().id;
  const errorMessage = useMemberErrorMessage();
  const memberError = useMemberError();
  const paged = usePagedSearch();
  const { q, page, size } = paged;
  const load = useCallback(() => list({ q, page, size }), [list, q, page, size]);
  const { status, data, error, reload } = useRemote(load);
  const [pending, setPending] = useState<Pending>(null);
  const [actionError, setActionError] = useState<MemberError | null>(null);
  // Members whose status request is running: their menu item is disabled, and a ref guards a
  // second fire before the state has rendered (PERF-006).
  const [busyIds, setBusyIds] = useState<ReadonlySet<number>>(new Set());
  const inFlight = useRef(new Set<number>());
  const heading = useRef<HTMLHeadingElement>(null);
  const headingId = useId();
  const focusReturn = useMenuFocusReturn(heading);
  const rows = data?.content ?? [];
  const staffRoles: RoleOption[] = STAFF_ROLES.map((role) => ({
    value: role,
    label: t(`roles.${role}`),
  }));

  async function changeStatus(member: TenantMember, next: TenantMemberStatus) {
    if (inFlight.current.has(member.membershipId)) return;
    inFlight.current.add(member.membershipId);
    setBusyIds(new Set(inFlight.current));
    try {
      await saasAuthResellerApi.changeTenantMemberStatus(tenantId, member.membershipId, next);
      showToast(t(next === 'DISABLED' ? 'members.toast.disabled' : 'members.toast.enabled'));
      reload();
    } finally {
      inFlight.current.delete(member.membershipId);
      setBusyIds(new Set(inFlight.current));
    }
  }

  /** Enable is one click (it restores access); its error is the page's alert. */
  async function enable(member: TenantMember) {
    setActionError(null);
    try {
      await changeStatus(member, 'ACTIVE');
    } catch (err) {
      setActionError(memberError(err));
    }
  }

  function dialogClosed() {
    setPending(null);
    focusReturn.dialogClosed();
  }

  function actionsFor(member: TenantMember): RowAction[] {
    if (!canWrite) return [];
    const disabled = member.status === 'DISABLED';
    const actions: RowAction[] = [];
    if (kind === 'staff' && STAFF_ROLES.includes(member.role as TenantRole)) {
      actions.push({
        key: 'role',
        label: t('members.action.changeRole'),
        onSelect: () => setPending({ kind: 'role', member }),
      });
    }
    actions.push(
      {
        key: 'status',
        label: disabled ? t('members.action.enable') : t('members.action.disable'),
        disabled: busyIds.has(member.membershipId),
        onSelect: () => (disabled ? void enable(member) : setPending({ kind: 'disable', member })),
      },
      {
        key: 'remove',
        label: t('members.action.remove'),
        onSelect: () => setPending({ kind: 'remove', member }),
      },
    );
    return actions;
  }

  const target = pending?.member ?? null;
  const who = (member: TenantMember | null) => member?.email || member?.name || '';

  return (
    <Box>
      <SearchField paged={paged} label={t('people.search')} />
      <ActionErrorAlert error={actionError} sx={{ mb: 2 }} />
      <Typography
        variant="h2"
        component="h2"
        id={headingId}
        tabIndex={-1}
        ref={heading}
        sx={visuallyHidden}
      >
        {t(`people.tab.${kind}`)}
      </Typography>
      {status === 'error' ? <ApiErrorState what={t('errors.what.people')} error={error} onRetry={reload} /> : null}
      {status === 'loading' && data === null ? (
        <Box role="status" aria-label={t('common.loading')}>
          <Skeleton variant="rounded" height={240} />
        </Box>
      ) : null}
      {status !== 'error' && data !== null && rows.length === 0 ? (
        q ? (
          <EmptyState kind="noMatch" title={t('members.noMatch')} onClear={paged.clear} />
        ) : (
          <EmptyState kind="none" title={t(`people.empty.${kind}`)} />
        )
      ) : null}
      {status !== 'error' && data !== null && rows.length > 0 ? (
        <Paper sx={{ overflow: 'hidden' }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-labelledby={headingId}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('members.col.name')}</TableCell>
                  <TableCell>{t('members.col.email')}</TableCell>
                  <TableCell>{t('members.col.role')}</TableCell>
                  <TableCell>{t('members.col.status')}</TableCell>
                  <TableCell>{t('members.col.created')}</TableCell>
                  {canWrite ? (
                    <TableCell align="right">{t('members.col.actions')}</TableCell>
                  ) : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((member) => (
                  <TableRow key={member.membershipId}>
                    <TableCell>{member.name ?? t('members.none')}</TableCell>
                    <TableCell component="th" scope="row" sx={{ overflowWrap: 'anywhere' }}>
                      {member.email}
                    </TableCell>
                    <TableCell>{roleLabel(member.role, t)}</TableCell>
                    <TableCell>
                      <StatusChip domain="member" status={member.status} />
                    </TableCell>
                    <TableCell>{dayOf(member.createdAt, locale, t)}</TableCell>
                    {canWrite ? (
                      <TableCell align="right">
                        <RowActionsMenu
                          label={t('members.actionsFor', { who: who(member) })}
                          actions={actionsFor(member)}
                          opener={focusReturn.opener}
                        />
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Pager paged={paged} count={data.totalElements} />
        </Paper>
      ) : null}
      <RoleDialog
        who={pending?.kind === 'role' ? who(target) : null}
        initial={target?.role ?? ''}
        options={staffRoles}
        onSubmit={async (role) => {
          if (!target) return;
          await saasAuthResellerApi.changeTenantMemberRole(
            tenantId,
            target.membershipId,
            role as TenantRole,
          );
          showToast(t('members.toast.roleChanged'));
          reload();
        }}
        onClose={dialogClosed}
        errorMessage={errorMessage}
      />
      <ConfirmDialog
        open={pending?.kind === 'disable'}
        title={t('people.disableTitle', { who: who(target) })}
        consequence={t('people.disableBody')}
        confirmLabel={t('members.action.disable')}
        destructive
        onConfirm={async () => {
          if (target) await changeStatus(target, 'DISABLED');
        }}
        onCancel={dialogClosed}
        errorMessage={errorMessage}
      />
      <ConfirmDialog
        open={pending?.kind === 'remove'}
        title={t('people.removeTitle', { who: who(target) })}
        consequence={t('people.removeBody')}
        confirmLabel={t('members.action.remove')}
        destructive
        onConfirm={async () => {
          if (!target) return;
          await saasAuthResellerApi.removeTenantMember(tenantId, target.membershipId);
          showToast(t('members.toast.removed'));
          focusReturn.rowGone();
          reload();
        }}
        onCancel={dialogClosed}
        errorMessage={errorMessage}
      />
    </Box>
  );
}
